import { Timbre, kitPieceLabelsFor } from "./audio";

/**
 * Scene-wide custom instrument: the single source of truth for the ordered list
 * of microphone recordings that the `mic` timbre plays. One shared list per
 * device (not per object, not per scene) — every `mic` placed object reads the
 * same recordings through its degree (pitchIndex → kitPieceIndex). The audio
 * blobs live only in IndexedDB on this device: they survive reload but are never
 * written into the scene's localStorage / JSON export / share upload, so a scene
 * opened elsewhere keeps `timbreId="mic"` yet sounds silent rather than erroring.
 */

const DB_NAME = "resonella";
const STORE = "micPitches";
const IMPORTED_STORE = "importedMic";
const DB_VERSION = 2;

/** Order-sorted recording metadata exposed to the UI; the audio blob stays internal. */
export interface MicPitch {
  id: string;
  name: string;
  order: number;
  mime: string;
  createdAt: number;
}

interface MicRecord extends MicPitch {
  blob: Blob;
  trimStart?: number;
  trimEnd?: number;
}

/**
 * The content-addressable description of one mic-kit recording carried inside a
 * shared scene JSON. The audio bytes are never embedded — the `hash` (SHA-256 of
 * the blob) points at the recording uploaded to the public `mic` bucket, and an
 * importer fetches it by that hash. `order` reproduces the kit play order so a
 * mic object's degree resolves to the same recording it did for the author.
 */
export interface MicManifestEntry {
  hash: string;
  name: string;
  order: number;
  mime: string;
  durationMs: number;
  trimStart?: number;
  trimEnd?: number;
}

/** A blob keyed only by its content hash, the device-local Layer B cache for imported scenes. */
interface ImportedMicRecord {
  hash: string;
  blob: Blob;
  mime: string;
}

type Decoder = (data: ArrayBuffer) => Promise<AudioBuffer>;

/** Lowercase hex SHA-256 of the given bytes (the content address used for mic-asset dedup and manifest links). */
export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b: number): string => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Recordings sorted by their stored order, ties broken by creation time so the sort is stable across reloads. */
export function sortByOrder<T extends { order: number; createdAt: number }>(list: readonly T[]): T[] {
  return [...list].sort((a: T, b: T): number => a.order - b.order || a.createdAt - b.createdAt);
}

/** Contiguous 0..n-1 order assignment over an already-ordered array (returns new objects only where order changed). */
export function renumber<T extends { order: number }>(ordered: readonly T[]): T[] {
  return ordered.map((item: T, i: number): T => (item.order === i ? item : { ...item, order: i }));
}

/**
 * The order-sorted array with `id` moved one slot in `dir` (-1 left / +1 right),
 * then renumbered to stay contiguous. A move that would leave the array bounds
 * (or an unknown id) returns the input unchanged, so the end recordings can't be
 * pushed out of range.
 */
export function moveByOne<T extends { id: string; order: number; createdAt: number }>(
  list: readonly T[],
  id: string,
  dir: -1 | 1,
): T[] {
  const ordered = sortByOrder(list);
  const i = ordered.findIndex((item: T): boolean => item.id === id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= ordered.length) {
    return ordered;
  }
  const swapped = [...ordered];
  [swapped[i], swapped[j]] = [swapped[j], swapped[i]];
  return renumber(swapped);
}

function openDb(): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve: (db: IDBDatabase) => void, reject: (e: unknown) => void): void => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (): void => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(IMPORTED_STORE)) {
        db.createObjectStore(IMPORTED_STORE, { keyPath: "hash" });
      }
    };
    req.onsuccess = (): void => resolve(req.result);
    req.onerror = (): void => reject(req.error);
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve: () => void, reject: (e: unknown) => void): void => {
    tx.oncomplete = (): void => resolve();
    tx.onerror = (): void => reject(tx.error);
    tx.onabort = (): void => reject(tx.error);
  });
}

class MicStore {
  private records: MicRecord[] = [];
  private readonly buffers = new Map<string, AudioBuffer | null>();
  private readonly hashes = new Map<string, string>();
  private readonly durations = new Map<string, number>();
  private readonly importedBuffers = new Map<string, AudioBuffer | null>();
  private activeManifest: MicManifestEntry[] | null = null;
  private readonly subscribers = new Set<() => void>();
  private decoder: Decoder | null = null;
  private db: IDBDatabase | null = null;
  private attached = false;

  /**
   * Wires the decode function (the AudioEngine owns the AudioContext), opens the
   * IndexedDB store, loads every persisted recording and decodes its blob into
   * the buffer cache, then notifies subscribers. Idempotent — a second call is a
   * no-op. A missing IndexedDB (unsupported environment) or a load failure logs
   * once and leaves the store empty rather than crashing.
   */
  async attach(decode: Decoder): Promise<void> {
    this.decoder = decode;
    if (this.attached) {
      return;
    }
    this.attached = true;
    if (typeof indexedDB === "undefined") {
      return;
    }
    try {
      this.db = await openDb();
      const tx = this.db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).getAll();
      const all = await new Promise<MicRecord[]>((resolve: (r: MicRecord[]) => void, reject: (e: unknown) => void): void => {
        req.onsuccess = (): void => resolve(req.result as MicRecord[]);
        req.onerror = (): void => reject(req.error);
      });
      this.records = sortByOrder(all);
    } catch (e: unknown) {
      console.error("micStore load failed", e);
      this.records = [];
    }
    await Promise.all(this.records.map((r: MicRecord): Promise<void> => this.loadRecord(r)));
    this.notify();
  }

  /** Decodes a Layer A record into the buffer cache and computes its content hash so manifests can be built synchronously. */
  private async loadRecord(r: MicRecord): Promise<void> {
    const bytes = await r.blob.arrayBuffer();
    this.hashes.set(r.id, await sha256Hex(bytes));
    await this.decodeRecord(r, bytes);
  }

  private async decodeRecord(r: MicRecord, bytes?: ArrayBuffer): Promise<void> {
    if (this.decoder === null) {
      return;
    }
    try {
      const buf = await this.decoder(bytes ?? (await r.blob.arrayBuffer()));
      this.buffers.set(r.id, buf);
      this.durations.set(r.id, buf.duration * 1000);
    } catch (e: unknown) {
      console.error(`micStore decode failed: ${r.id}`, e);
      this.buffers.set(r.id, null);
    }
  }

  /** Order-sorted recording metadata (no blobs). */
  list(): MicPitch[] {
    return this.records.map((r: MicRecord): MicPitch => ({ id: r.id, name: r.name, order: r.order, mime: r.mime, createdAt: r.createdAt }));
  }

  /**
   * Recording names in play order, resolved from the active layer: the imported
   * scene's manifest when one is active (Layer B), else this device's own
   * recordings (Layer A). The play-order resolvers all branch the same way so a
   * mic object's degree never crosses layers (an imported scene must not sound
   * with this device's recordings).
   */
  names(): string[] {
    if (this.activeManifest !== null) {
      return this.activeManifest.map((e: MicManifestEntry): string => e.name);
    }
    return this.records.map((r: MicRecord): string => r.name);
  }

  /** Decoded buffers in play order; an entry is null while still pending / if decode failed. */
  buffersInOrder(): (AudioBuffer | null)[] {
    if (this.activeManifest !== null) {
      return this.activeManifest.map((e: MicManifestEntry): AudioBuffer | null => this.importedBuffers.get(e.hash) ?? null);
    }
    return this.records.map((r: MicRecord): AudioBuffer | null => this.buffers.get(r.id) ?? null);
  }

  /** Trim regions (seconds) in play order; null means play the full buffer (a recording saved before trim metadata existed). */
  trimsInOrder(): ({ start: number; end: number } | null)[] {
    const trimOf = (trimStart: number | undefined, trimEnd: number | undefined): { start: number; end: number } | null =>
      trimStart !== undefined && trimEnd !== undefined ? { start: trimStart, end: trimEnd } : null;
    if (this.activeManifest !== null) {
      return this.activeManifest.map((e: MicManifestEntry): { start: number; end: number } | null => trimOf(e.trimStart, e.trimEnd));
    }
    return this.records.map((r: MicRecord): { start: number; end: number } | null => trimOf(r.trimStart, r.trimEnd));
  }

  /** Count of this device's own recordings (Layer A); the editable library, independent of any active imported scene. */
  count(): number {
    return this.records.length;
  }

  /** True while an imported scene's mic kit (Layer B) is the active playback source, so the edit UI hides its mutating controls. */
  isImported(): boolean {
    return this.activeManifest !== null;
  }

  async add(blob: Blob, name: string, trimStart?: number, trimEnd?: number): Promise<void> {
    const rec: MicRecord = {
      id: crypto.randomUUID(),
      name,
      order: this.records.length,
      mime: blob.type,
      blob,
      createdAt: Date.now(),
      trimStart,
      trimEnd,
    };
    await this.put([rec]);
    this.records = sortByOrder([...this.records, rec]);
    await this.loadRecord(rec);
    this.notify();
  }

  async rename(id: string, name: string): Promise<void> {
    const rec = this.records.find((r: MicRecord): boolean => r.id === id);
    if (rec === undefined) {
      return;
    }
    rec.name = name;
    await this.put([rec]);
    this.notify();
  }

  async remove(id: string): Promise<void> {
    if (!this.records.some((r: MicRecord): boolean => r.id === id)) {
      return;
    }
    const remaining = renumber(this.records.filter((r: MicRecord): boolean => r.id !== id));
    await this.deleteAndRepersist(id, remaining);
    this.records = remaining;
    this.buffers.delete(id);
    this.hashes.delete(id);
    this.durations.delete(id);
    this.notify();
  }

  async reorder(id: string, dir: -1 | 1): Promise<void> {
    const next = moveByOne(this.records, id, dir);
    if (next === this.records || this.sameOrder(next)) {
      return;
    }
    await this.put(next);
    this.records = next;
    this.notify();
  }

  subscribe(cb: () => void): () => void {
    this.subscribers.add(cb);
    return (): void => {
      this.subscribers.delete(cb);
    };
  }

  private sameOrder(next: readonly MicRecord[]): boolean {
    return next.length === this.records.length && next.every((r: MicRecord, i: number): boolean => r.id === this.records[i].id);
  }

  private notify(): void {
    for (const cb of this.subscribers) {
      cb();
    }
  }

  private async put(records: readonly MicRecord[]): Promise<void> {
    if (this.db === null) {
      return;
    }
    const tx = this.db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    for (const r of records) {
      store.put(r);
    }
    await txDone(tx);
  }

  private async deleteAndRepersist(id: string, remaining: readonly MicRecord[]): Promise<void> {
    if (this.db === null) {
      return;
    }
    const tx = this.db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    store.delete(id);
    for (const r of remaining) {
      store.put(r);
    }
    await txDone(tx);
  }

  /**
   * The content-addressable manifest of the active mic kit, in play order: the
   * imported scene's manifest verbatim when one is active, else this device's own
   * recordings keyed by their content hash. Synchronous — Layer A hashes are
   * precomputed at load / add time — so it can ride inside the synchronous
   * sceneBundle(). A record whose hash has not finished computing is dropped
   * rather than emitted hashless.
   */
  buildManifest(): MicManifestEntry[] {
    if (this.activeManifest !== null) {
      return this.activeManifest.map((e: MicManifestEntry): MicManifestEntry => ({ ...e }));
    }
    const out: MicManifestEntry[] = [];
    sortByOrder(this.records).forEach((r: MicRecord, i: number): void => {
      const hash = this.hashes.get(r.id);
      if (hash === undefined) {
        console.error(`micStore manifest skipped (hash pending): ${r.id}`);
        return;
      }
      out.push({
        hash,
        name: r.name,
        order: i,
        mime: r.mime,
        durationMs: Math.round(this.durations.get(r.id) ?? 0),
        ...(r.trimStart !== undefined && r.trimEnd !== undefined ? { trimStart: r.trimStart, trimEnd: r.trimEnd } : {}),
      });
    });
    return out;
  }

  /** The blob backing a content hash, looked up across this device's own recordings (Layer A) then the imported cache (Layer B), or null when absent. */
  async blobForHash(hash: string): Promise<Blob | null> {
    for (const r of this.records) {
      if (this.hashes.get(r.id) === hash) {
        return r.blob;
      }
    }
    const imported = await this.getImported(hash);
    return imported === null ? null : imported.blob;
  }

  /**
   * Switches the active playback source: a non-null manifest makes the imported
   * scene's kit (Layer B) play, null returns to this device's own recordings
   * (Layer A). Setting an imported manifest requires its buffers to already be
   * in the Layer B cache (see resolveManifest).
   */
  setActiveManifest(manifest: MicManifestEntry[] | null): void {
    this.activeManifest =
      manifest === null
        ? null
        : [...manifest].sort((a: MicManifestEntry, b: MicManifestEntry): number => a.order - b.order).map((e: MicManifestEntry): MicManifestEntry => ({ ...e }));
    this.notify();
  }

  /**
   * Frees the decoded Layer B buffers that are not part of the active manifest, so
   * a browse session's accumulated kits (settled + prefetched neighbours) do not
   * grow memory without bound. Called when leaving the feed. The IndexedDB blobs
   * are kept, so revisiting a freed kit re-decodes from cache without re-fetching.
   */
  evictInactiveImported(): void {
    const keep = new Set<string>((this.activeManifest ?? []).map((e: MicManifestEntry): string => e.hash));
    for (const hash of [...this.importedBuffers.keys()]) {
      if (!keep.has(hash)) {
        this.importedBuffers.delete(hash);
      }
    }
  }

  /**
   * Resolves an imported scene's mic kit into the Layer B cache without
   * activating it, so a caller can apply its own latest-wins gate between the
   * (slow) fetch and the (instant) activation — a stale resolve from a card the
   * user scrolled past must not clobber the active layer. Each entry's blob is
   * loaded from IndexedDB when already cached, else fetched from its public URL
   * and persisted by hash; the bytes are decoded for playback. A fetch / decode
   * failure leaves that piece silent (null buffer) and is logged — it is never
   * substituted with another recording. Activate with setActiveManifest once the
   * gate confirms this kit is still the wanted one.
   */
  async resolveManifest(manifest: MicManifestEntry[], urlFor: (entry: MicManifestEntry) => string): Promise<void> {
    await Promise.all(manifest.map((e: MicManifestEntry): Promise<void> => this.resolveImported(e, urlFor)));
  }

  private async resolveImported(entry: MicManifestEntry, urlFor: (entry: MicManifestEntry) => string): Promise<void> {
    if (this.importedBuffers.has(entry.hash) && this.importedBuffers.get(entry.hash) !== null) {
      return;
    }
    let record = await this.getImported(entry.hash);
    if (record === null) {
      try {
        const res = await fetch(urlFor(entry));
        if (!res.ok) {
          throw new Error(`mic fetch ${res.status}`);
        }
        const blob = await res.blob();
        record = { hash: entry.hash, blob, mime: entry.mime };
        await this.putImported(record);
      } catch (e: unknown) {
        console.error(`micStore import fetch failed: ${entry.hash}`, e);
        this.importedBuffers.set(entry.hash, null);
        return;
      }
    }
    if (this.decoder === null) {
      this.importedBuffers.set(entry.hash, null);
      return;
    }
    try {
      this.importedBuffers.set(entry.hash, await this.decoder(await record.blob.arrayBuffer()));
    } catch (e: unknown) {
      console.error(`micStore import decode failed: ${entry.hash}`, e);
      this.importedBuffers.set(entry.hash, null);
    }
  }

  private getImported(hash: string): Promise<ImportedMicRecord | null> {
    if (this.db === null) {
      return Promise.resolve(null);
    }
    const tx = this.db.transaction(IMPORTED_STORE, "readonly");
    const req = tx.objectStore(IMPORTED_STORE).get(hash);
    return new Promise<ImportedMicRecord | null>((resolve: (r: ImportedMicRecord | null) => void, reject: (e: unknown) => void): void => {
      req.onsuccess = (): void => resolve((req.result as ImportedMicRecord | undefined) ?? null);
      req.onerror = (): void => reject(req.error);
    });
  }

  private async putImported(record: ImportedMicRecord): Promise<void> {
    if (this.db === null) {
      return;
    }
    const tx = this.db.transaction(IMPORTED_STORE, "readwrite");
    tx.objectStore(IMPORTED_STORE).put(record);
    await txDone(tx);
  }
}

export const micStore = new MicStore();

/**
 * The piece labels a kit-style timbre shows over its objects and in its piece
 * slider: the live recording names for `mic` (length grows / shrinks with the
 * shared list), or the static catalog labels for drum, or null for a
 * pitched timbre. Mic-aware superset of `kitPieceLabelsFor`.
 */
export function kitLabelsForTimbre(id: Timbre): readonly string[] | null {
  return id === "mic" ? micStore.names() : kitPieceLabelsFor(id);
}
